import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { ArrowLeft, Edit, Trash2, Check, X } from 'lucide-react';
import { Student, PreAssessedFee, PricingTier, Fee } from '../../types';
import { useData } from '../../contexts/DataContext';

interface StudentPreAssessedFeesProps {
  student: Student;
  onBack: () => void;
}

export function StudentPreAssessedFees({ student, onBack }: StudentPreAssessedFeesProps) {
  const { preAssessedFees: allPreAssessedFees, setPreAssessedFees: setAllPreAssessedFees, fees } = useData();
  const [preAssessedFees, setPreAssessedFees] = useState<PreAssessedFee[]>(
    allPreAssessedFees.filter(paf => paf.studentId === student.id)
  );
  const [editingFee, setEditingFee] = useState<PreAssessedFee | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<PreAssessedFee>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [feeToDelete, setFeeToDelete] = useState<PreAssessedFee | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const getFee = (feeId: string): Fee | undefined => {
    return fees.find(f => f.id === feeId);
  };

  const getPriceForTier = (fee: Fee, tier: PricingTier): number => {
    switch (tier) {
      case 'full': return fee.basePrice;
      case 'reduced': return fee.reducedPrice;
      case 'free': return fee.freePrice;
      default: return fee.basePrice;
    }
  };

  const handleEditClick = (paf: PreAssessedFee) => {
    setEditingFee(paf);
    setEditFormData({
      quantity: paf.quantity,
      pricingTier: paf.pricingTier,
      dueDate: paf.dueDate,
      amountPaid: paf.amountPaid,
    });
  };

  const handleSaveEdit = () => {
    if (!editingFee) return;

    const updatedFees = preAssessedFees.map(paf =>
      paf.id === editingFee.id
        ? {
            ...paf,
            quantity: editFormData.quantity ?? paf.quantity,
            pricingTier: editFormData.pricingTier ?? paf.pricingTier,
            dueDate: editFormData.dueDate ?? paf.dueDate,
            amountPaid: editFormData.amountPaid ?? paf.amountPaid,
          }
        : paf
    );

    setPreAssessedFees(updatedFees);

    const index = mockPreAssessedFees.findIndex(paf => paf.id === editingFee.id);
    if (index > -1) {
      mockPreAssessedFees[index] = {
        ...mockPreAssessedFees[index],
        quantity: editFormData.quantity ?? mockPreAssessedFees[index].quantity,
        pricingTier: editFormData.pricingTier ?? mockPreAssessedFees[index].pricingTier,
        dueDate: editFormData.dueDate ?? mockPreAssessedFees[index].dueDate,
        amountPaid: editFormData.amountPaid ?? mockPreAssessedFees[index].amountPaid,
      };
    }

    setSuccessMessage('Fee assessment updated successfully');
    setEditingFee(null);
    setEditFormData({});
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDeleteClick = (paf: PreAssessedFee) => {
    setFeeToDelete(paf);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!feeToDelete) return;

    const updatedFees = preAssessedFees.filter(paf => paf.id !== feeToDelete.id);
    setPreAssessedFees(updatedFees);

    const index = mockPreAssessedFees.findIndex(paf => paf.id === feeToDelete.id);
    if (index > -1) {
      mockPreAssessedFees.splice(index, 1);
    }

    setSuccessMessage('Fee assessment removed successfully');
    setDeleteConfirmOpen(false);
    setFeeToDelete(null);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowLeft size={18} />}
          onClick={onBack}
          sx={{ mb: 2 }}
        >
          Back to Students
        </Button>

        <Card>
          <CardContent>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Pre-Assessed Fees
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body1" color="text.secondary">
                <strong>Student:</strong> {student.firstName} {student.lastName}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                <strong>ID:</strong> {student.studentIdNumber}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                <strong>Grade:</strong> {student.grade}
              </Typography>
              <Chip
                label={`${student.pricingTier} tier`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ height: successMessage ? 'auto' : 0, mb: successMessage ? 3 : 0, overflow: 'hidden', transition: 'margin 0.2s ease' }}>
        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}
      </Box>

      {preAssessedFees.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Pre-Assessed Fees
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This student has no pre-assessed fees.
          </Typography>
        </Paper>
      ) : (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Financial Summary
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mt: 2 }}>
                {(() => {
                  const summary = preAssessedFees.reduce((acc, paf) => {
                    const fee = getFee(paf.feeId);
                    if (!fee) return acc;

                    const unitPrice = getPriceForTier(fee, paf.pricingTier);
                    const total = unitPrice * paf.quantity;
                    const paid = paf.amountPaid || 0;
                    const balance = total - paid;

                    acc.totalAssessed += total;
                    acc.totalPaid += paid;
                    acc.totalBalance += balance;

                    if (paf.status === 'overdue') {
                      acc.pastDue += balance;
                      acc.overdueCount++;
                    } else if (paf.status === 'pending') {
                      acc.pendingCount++;
                    } else if (paf.status === 'paid') {
                      acc.paidCount++;
                    }

                    return acc;
                  }, {
                    totalAssessed: 0,
                    totalPaid: 0,
                    totalBalance: 0,
                    pastDue: 0,
                    pendingCount: 0,
                    paidCount: 0,
                    overdueCount: 0
                  });

                  return (
                    <>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Total Assessed
                        </Typography>
                        <Typography variant="h5" fontWeight={700}>
                          ${summary.totalAssessed.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {preAssessedFees.length} fee{preAssessedFees.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Total Paid
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color="success.main">
                          ${summary.totalPaid.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {summary.paidCount} paid
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Balance Due
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color={summary.totalBalance > 0 ? 'warning.main' : 'success.main'}>
                          ${summary.totalBalance.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {summary.pendingCount} pending
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Past Due
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color={summary.pastDue > 0 ? 'error.main' : 'text.primary'}>
                          ${summary.pastDue.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {summary.overdueCount} overdue
                        </Typography>
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </CardContent>
          </Card>

          <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fee Name</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Pricing Tier</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Amount Paid</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preAssessedFees.map((paf) => {
                const fee = getFee(paf.feeId);
                if (!fee) return null;

                const unitPrice = getPriceForTier(fee, paf.pricingTier);
                const total = unitPrice * paf.quantity;
                const balance = total - (paf.amountPaid || 0);

                return (
                  <TableRow key={paf.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {fee.name}
                      </Typography>
                      {fee.course && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {fee.course}
                        </Typography>
                      )}
                      {fee.activityPassPricing?.enabled && (
                        <Chip
                          label={`Activity Pass: ${fee.activityPassPricing.isFree ? 'FREE' : `$${fee.activityPassPricing.price}`} (x${fee.activityPassPricing.quantity})`}
                          size="small"
                          sx={{
                            mt: 0.5,
                            bgcolor: '#f0fdf4',
                            color: '#16a34a',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 20,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{paf.quantity}</TableCell>
                    <TableCell>
                      <Chip label={paf.pricingTier} size="small" />
                    </TableCell>
                    <TableCell>${unitPrice.toFixed(2)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        ${total.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {paf.amountPaid ? `$${paf.amountPaid.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={balance > 0 ? 'error.main' : 'success.main'}
                      >
                        ${balance.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(paf.dueDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={paf.status}
                        size="small"
                        color={getStatusColor(paf.status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(paf)}
                          color="primary"
                        >
                          <Edit size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(paf)}
                          color="error"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      <Dialog
        open={editingFee !== null}
        onClose={() => setEditingFee(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Fee Assessment
          {editingFee && getFee(editingFee.feeId) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {getFee(editingFee.feeId)!.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {editingFee && getFee(editingFee.feeId) && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                value={editFormData.quantity ?? editingFee.quantity}
                onChange={(e) => setEditFormData({ ...editFormData, quantity: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
              />

              <FormControl fullWidth>
                <InputLabel>Pricing Tier</InputLabel>
                <Select
                  value={editFormData.pricingTier ?? editingFee.pricingTier}
                  onChange={(e) => setEditFormData({ ...editFormData, pricingTier: e.target.value as PricingTier })}
                  label="Pricing Tier"
                >
                  <MenuItem value="full">Full Price</MenuItem>
                  <MenuItem value="reduced">Reduced Price</MenuItem>
                  <MenuItem value="free">Free</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Due Date"
                type="date"
                fullWidth
                value={
                  editFormData.dueDate
                    ? new Date(editFormData.dueDate).toISOString().split('T')[0]
                    : new Date(editingFee.dueDate).toISOString().split('T')[0]
                }
                onChange={(e) => setEditFormData({ ...editFormData, dueDate: new Date(e.target.value) })}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Amount Paid"
                type="number"
                fullWidth
                value={editFormData.amountPaid ?? editingFee.amountPaid ?? 0}
                onChange={(e) => setEditFormData({ ...editFormData, amountPaid: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Enter the amount already paid towards this fee"
              />

              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Price Calculation:
                </Typography>
                <Typography variant="body2">
                  Unit Price: ${getPriceForTier(
                    getFee(editingFee.feeId)!,
                    (editFormData.pricingTier ?? editingFee.pricingTier) as PricingTier
                  ).toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  Quantity: {editFormData.quantity ?? editingFee.quantity}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  Total: ${(
                    getPriceForTier(
                      getFee(editingFee.feeId)!,
                      (editFormData.pricingTier ?? editingFee.pricingTier) as PricingTier
                    ) * (editFormData.quantity ?? editingFee.quantity)
                  ).toFixed(2)}
                </Typography>
                <Typography variant="body2" fontWeight={600} color="primary.main">
                  Balance: ${(
                    getPriceForTier(
                      getFee(editingFee.feeId)!,
                      (editFormData.pricingTier ?? editingFee.pricingTier) as PricingTier
                    ) * (editFormData.quantity ?? editingFee.quantity) -
                    (editFormData.amountPaid ?? editingFee.amountPaid ?? 0)
                  ).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingFee(null)} startIcon={<X size={16} />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            startIcon={<Check size={16} />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Fee Assessment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this fee assessment? This action cannot be undone.
          </Typography>
          {feeToDelete && getFee(feeToDelete.feeId) && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {getFee(feeToDelete.feeId)!.name}
              </Typography>
              <Typography variant="body2">
                Quantity: {feeToDelete.quantity}
              </Typography>
              <Typography variant="body2">
                Total: ${(
                  getPriceForTier(getFee(feeToDelete.feeId)!, feeToDelete.pricingTier) *
                  feeToDelete.quantity
                ).toFixed(2)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            startIcon={<Trash2 size={16} />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
